"use client";

import { useCallback, useMemo, useState } from "react";
import { capturePostHogEvent } from "@/lib/posthog";
import { Search } from "lucide-react";
import { useTranslations } from 'next-intl';

import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import type { FaqItem } from "@/data/faqs";
import { AnimatedSection } from "@/components/ui/animated-section";

type FaqClientWrapperProps = {
  faqs: FaqItem[];
};

/**
 * Client component wrapper that adds interactivity (search, accordion) to FAQ content.
 * The answers are always rendered in HTML (server-side), but hidden/shown via CSS for UX.
 * This ensures SEO crawlers can see all content while maintaining accordion UX.
 */
export function FaqClientWrapper({ faqs }: FaqClientWrapperProps) {
  const t = useTranslations('faq');
  const [query, setQuery] = useState("");

  const filteredFaqs = useMemo(() => {
    if (!query.trim()) {
      return faqs;
    }

    const normalizedQuery = query.toLowerCase();
    return faqs.filter((faq) => {
      const haystack = `${faq.question} ${faq.answer}`.toLowerCase();
      return normalizedQuery.split(/\s+/).every((word) => haystack.includes(word));
    });
  }, [faqs, query]);

  const handleAccordionChange = useCallback((value: string) => {
    if (value) {
      const index = parseInt(value.replace("faq-", ""), 10);
      const question = filteredFaqs[index]?.question;
      if (question) capturePostHogEvent("faq_opened", { question });
    }
  }, [filteredFaqs]);

  return (
    <AnimatedSection animation="fade-up" delay={0}>
      <div className="p-6 sm:p-8">
        <AnimatedSection animation="fade-up" delay={100}>
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">{t('search.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('search.description')}
              </p>
            </div>

            <div className="relative w-full md:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('search.placeholder')}
                className="pl-9"
                aria-label={t('search.ariaLabel')}
              />
            </div>
          </div>
        </AnimatedSection>

        <div className="mt-6">
          {filteredFaqs.length > 0 ? (
            <Accordion type="single" collapsible onValueChange={handleAccordionChange}>
              {filteredFaqs.map((faq, index) => (
                <AnimatedSection
                  key={faq.question}
                  animation="fade-up"
                  delay={200 + index * 50}
                >
                  <AccordionItem value={`faq-${index}`}>
                    <AccordionTrigger className="text-left text-base font-medium text-foreground">
                      {/* H2 for each question - proper heading hierarchy for SEO */}
                      {/* Note: Answers are also rendered server-side in hidden div above for maximum SEO */}
                      <h2 className="text-base font-medium text-foreground pr-2">{faq.question}</h2>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {/* Answer is always rendered in HTML for SEO, even when accordion is collapsed */}
                      {/* Radix UI Accordion renders content in DOM but hides with CSS - perfect for SEO */}
                      {/* Additionally, answers are rendered server-side in hidden div for maximum crawler visibility */}
                      <div>{faq.answer}</div>
                    </AccordionContent>
                  </AccordionItem>
                </AnimatedSection>
              ))}
            </Accordion>
          ) : (
            <AnimatedSection animation="fade-up" delay={200}>
              <div className="rounded-xl border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
                {t('search.noResults', { query })}
              </div>
            </AnimatedSection>
          )}
        </div>
      </div>
    </AnimatedSection>
  );
}

